<script setup lang="ts">
import { ChevronLeft, ChevronRight, Trash2 } from "@lucide/vue";
import { useI18n } from "vue-i18n";

import type { MasterPortfolioPhoto } from "@/entities/master-portfolio";
import { Badge } from "@/shared/ui";

const props = defineProps<{
  photo: MasterPortfolioPhoto;
  serviceName?: string;
  isFirst: boolean;
  isLast: boolean;
  reorderInFlight: boolean;
}>();

const emit = defineEmits<{
  moveBack: [];
  moveForward: [];
  delete: [];
}>();

const { t } = useI18n();
</script>

<template>
  <div
    role="listitem"
    class="relative aspect-square overflow-hidden rounded-md border border-border"
  >
    <img
      :src="props.photo.url"
      loading="lazy"
      :alt="props.serviceName
        ? t('masters.portfolio.a11y.taggedPhotoAlt', { service: props.serviceName })
        : t('masters.portfolio.a11y.photoAlt')"
      class="size-full object-cover"
    />

    <!-- Cover badge -->
    <Badge
      v-if="props.photo.position === 0"
      class="absolute left-1 top-1 text-xs"
    >
      {{ t('masters.portfolio.cover') }}
    </Badge>

    <!-- Service tag badge -->
    <Badge
      v-if="props.serviceName"
      variant="secondary"
      class="absolute right-1 top-1 max-w-[8rem] truncate text-xs"
    >
      {{ props.serviceName }}
    </Badge>

    <!-- Overlay action bar -->
    <div
      class="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-0.5 bg-background/80 px-0.5 py-0.5"
    >
      <button
        type="button"
        class="inline-flex size-6 items-center justify-center rounded text-foreground/70 transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        :disabled="props.isFirst || props.reorderInFlight"
        :aria-label="t('masters.portfolio.a11y.moveBack')"
        @click="emit('moveBack')"
      >
        <ChevronLeft class="size-3.5" aria-hidden="true" />
      </button>

      <button
        type="button"
        class="inline-flex size-6 items-center justify-center rounded text-foreground/70 transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        :disabled="props.isLast || props.reorderInFlight"
        :aria-label="t('masters.portfolio.a11y.moveForward')"
        @click="emit('moveForward')"
      >
        <ChevronRight class="size-3.5" aria-hidden="true" />
      </button>

      <button
        type="button"
        class="inline-flex size-6 items-center justify-center rounded text-foreground/70 transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        :aria-label="t('masters.portfolio.a11y.deletePhoto')"
        @click="emit('delete')"
      >
        <Trash2 class="size-3.5" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
